import { trimSpaceAndLowerCase } from "utils/trimSpaceAndLowerCase";
import * as Yup from "yup";

const validFileExtensions = {
  image: ["jpg", "gif", "png", "jpeg", "svg", "webp"],
};

function isValidFileType(fileName, fileType) {
  return (
    fileName &&
    validFileExtensions[fileType].indexOf(fileName.split(".").pop()) > -1
  );
}

export const uploadSchema = Yup.object().shape({
  mobileNumber: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Mobile number is required")
    .matches(
      /^(?:\+?91|0)?[6789]\d{9}$/,
      "Mobile number must be a valid Indian number"
    ),
  propertyUniqueId: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Unique Property ID is required"),
  oldPropertyTaxId: Yup.string()
    .notRequired()
    .trim("Can't contain trailing spaces")
    .strict(),
  ownerName: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Owner name is required"),
  fatherHusbandName: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Father/Husband name is required"),
  houseNumber: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("House number is required"),
});

export const loginSchema = Yup.object({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters"),
});

export const ChangePasswordSchema = Yup.object().shape({
  oldPassword: Yup.string()
    .trim("Can't contain trailing spaces")
    .min(8, "Password must be at least 8 characters")
    .required("Old Password is required"),
  newPassword: Yup.string()
    .trim("Can't contain trailing spaces")
    .notOneOf([Yup.ref("oldPassword"), null], "Can not be same as old password")
    .min(8, "Password must be at least 8 characters")
    .required("New Password is required"),

  confirmNewPassword: Yup.string()
    .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
    .required("Confirm Password is required"),
});

export const ResetPasswordSchema = Yup.object().shape({
  newPassword: Yup.string()
    .trim("Can't contain trailing spaces")
    .min(8, "Password must be at least 8 characters")
    .required("New Password is required"),

  confirmNewPassword: Yup.string()
    .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
    .required("Confirm Password is required"),
});

export const propertyEditSchema = Yup.object().shape({
  financialYear: Yup.string().required("Financial Year is required").strict(),
  ownerName: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Owner Name is required")
    .strict(),
  fatherName: Yup.string()
    .notRequired()
    .trim("Can't contain trailing spaces")
    .strict(),
  husbandName: Yup.string()
    .notRequired()
    .trim("Can't contain trailing spaces")
    .strict(),
  propertyAddress: Yup.string()
    .notRequired()
    .trim("Can't contain trailing spaces")
    .strict(),
  mobileNumber: Yup.string()
    .trim("Can't contain trailing spaces")
    .matches(
      /^(?:(?:\+|0{0,2})91(\s*|[-])?|[0]?)?([6789]\d{2}([-]?)\d{3}([-]?)\d{4})$/,
      "Mobile number is not valid"
    ),
  emailId: Yup.string()
    .email("Invalid email address")
    .trim("Can't contain trailing spaces"),

  zoneId: Yup.string()
    .required("Zone must be a string")
    .trim("Can't contain trailing spaces"),
  wardId: Yup.string()
    .required("Ward must be a string")
    .trim("Can't contain trailing spaces"),
  mohallaId: Yup.string()
    .required("Mohalla must be a string")
    .trim("Can't contain trailing spaces"),

  oldPropertyTaxId: Yup.string()
    .notRequired()
    .trim("Can't contain trailing spaces")
    .strict(),
  oldArv: Yup.number().positive("Must be a positive number"),
  houseNumber: Yup.string()
    .notRequired()
    .trim("Can't contain trailing spaces")
    .strict(),
  plotArea: Yup.number().positive("Must be a positive number").strict(),
  constType: Yup.string()
    .notRequired()
    .trim("Can't contain trailing spaces")
    .strict(),

  houseCategory: Yup.string()
    .required("House Category is required")
    .trim("Can't contain trailing spaces"),
  category: Yup.number().when("houseCategory", (houseCategory, schema) => {
    return trimSpaceAndLowerCase(houseCategory[0]) === "nonresidential"
      ? schema
          .required("Property Category is required")
          .typeError("Property Category must be a number")
      : schema.notRequired();
  }),
  subCategory: Yup.number().when("houseCategory", (houseCategory, schema) => {
    return trimSpaceAndLowerCase(houseCategory[0]) === "nonresidential"
      ? schema
          .required("Property Sub-Category is required")
          .typeError("Property Sub-Category must be a number")
      : schema.notRequired();
  }),

  occupancyStatus: Yup.string()
    .notRequired()
    .trim("Can't contain trailing spaces")
    .strict(),
  roadType: Yup.string()
    .notRequired()
    .trim("Can't contain trailing spaces")
    .strict(),
  roadWidth: Yup.string()
    .notRequired()
    .trim("Can't contain trailing spaces")
    .strict(),

  constructionYear: Yup.number().positive("Must be a positive number"),

  // facilitiesAvailable: Yup.boolean(),

  // facilities: Yup.string().notRequired().trim("Can't contain trailing spaces").strict(),

  isMetroRailProperty: Yup.boolean(),
  houseTaxApplicable: Yup.boolean().required("House Tax should be selected"),
  sewerageConnectionAvailable: Yup.boolean().required(
    "Sewer Connection should be selected"
  ),
  waterTaxApplicable: Yup.boolean().required("Water Tax should be selected"),
  sourceOfWater: Yup.string().notRequired(),

  isDisabled: Yup.boolean(),
  disabilityPercentage: Yup.number().when(
    "isDisabled",
    (isDisabled, schema) => {
      return isDisabled[0]
        ? schema
            .required("Disable Percentage is required")
            .positive("Should be positive disability")
            .max(100, "Max disability percentage can be 100%")
            .typeError("Disable Percentage must be a number")
        : schema.notRequired();
    }
  ),
  disabilityDocumentProof: Yup.string().when(
    "isDisabled",
    (isDisabled, schema) => {
      return isDisabled[0]
        ? schema.required(
            "Document proof is required when disability is applied"
          )
        : schema.notRequired();
    }
  ),
  isExDefensePersonalWidow: Yup.boolean(),
  exDefensePersonalDocumentProof: Yup.string().when(
    "isExDefensePersonalWidow",
    (isExDefensePersonalWidow, schema) => {
      return isExDefensePersonalWidow[0]
        ? schema.required("Document proof is required")
        : schema.notRequired();
    }
  ),
});

export const propertyAddSchema = Yup.object().shape({
  ownerName: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Owner Name is required"),
  fatherName: Yup.string(),
  husbandName: Yup.string(),
  propertyAddress: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Property address is required"),
  mobileNumber: Yup.string()
    .trim("Can't contain trailing spaces")
    .matches(
      /^(?:(?:\+|0{0,2})91(\s*|[-])?|[0]?)?([6789]\d{2}([-]?)\d{3}([-]?)\d{4})$/,
      "Mobile number is not valid"
    )
    .required("Mobile number is required"),
  emailId: Yup.string()
    .email("Invalid email address")
    .trim("Can't contain trailing spaces"),

  zoneId: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Zone is required"),
  wardId: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Ward is required"),
  mohallaId: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Mohalla is required"),

  houseNumber: Yup.string(),

  plotArea: Yup.number().required("Plot area is required"),
  constType: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Construction type is required"),

  houseCategory: Yup.string()
    .required("House Category is required")
    .trim("Can't contain trailing spaces"),

  category: Yup.number().when("houseCategory", (houseCategory, schema) => {
    return trimSpaceAndLowerCase(houseCategory[0]) === "nonresidential"
      ? schema
          .required("Property Category is required")
          .typeError("Property Category must be a number")
      : schema.notRequired();
  }),
  subCategory: Yup.number().when("houseCategory", (houseCategory, schema) => {
    return trimSpaceAndLowerCase(houseCategory[0]) === "nonresidential"
      ? schema
          .required("Property Sub-Category is required")
          .typeError("Property Sub-Category must be a number")
      : schema.notRequired();
  }),

  occupancyStatus: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Occupancy status is required"),
  roadType: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Road type is required"),
  roadWidth: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Road width is required"),

  constructionYear: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Construction year is required"),

  // facilitiesAvailable: Yup.array().required("Facilities are required"),

  sewerageConnectionAvailable: Yup.boolean().required(
    "Sewer Connection should be selected"
  ),
  waterTaxApplicable: Yup.boolean().required("Water Tax should be selected"),
  sourceOfWater: Yup.string().notRequired(),

  isDisabled: Yup.boolean().required("This is required"),
  disabilityPercentage: Yup.number().when(
    "isDisabled",
    (isDisabled, schema) => {
      return isDisabled[0]
        ? schema
            .required("Disable Percentage is required")
            .positive("Should be positive disability")
            .max(100, "Max disability percentage can be 100%")
            .typeError("Disable Percentage must be a number")
        : schema.notRequired();
    }
  ),
  disabilityDocumentProof: Yup.string().when(
    "isDisabled",
    (isDisabled, schema) => {
      return isDisabled[0]
        ? schema.required(
            "Document proof is required when disability is applied"
          )
        : schema.notRequired();
    }
  ),
  isExDefensePersonalWidow: Yup.boolean().required("This is required"),
  exDefensePersonalDocumentProof: Yup.string().when(
    "isExDefensePersonalWidow",
    (isExDefensePersonalWidow, schema) => {
      return isExDefensePersonalWidow[0]
        ? schema.required("Document proof is required")
        : schema.notRequired();
    }
  ),
});

export const floorEditSchema = Yup.object().shape({
  floorNumber: Yup.number().required("Floor Number is required"),
  rentType: Yup.string()
    .required("Rent Type is required")
    .trim("Can't contain trailing spaces"),
  rentUsage: Yup.string()
    .required("Rent Usage is required")
    .trim("Can't contain trailing spaces"),
  type: Yup.string()
    .required("Floor type is required")
    .trim("Can't contain trailing spaces"),
  category: Yup.number().when("houseCategory", (houseCategory, schema) => {
    return trimSpaceAndLowerCase(houseCategory[0]) === "Non Residential"
      ? schema
          .required("Property Category is required")
          .typeError("Property Category must be a number")
      : schema.notRequired();
  }),
  subCategory: Yup.number().when("houseCategory", (houseCategory, schema) => {
    return trimSpaceAndLowerCase(houseCategory[0]) === "Non Residential"
      ? schema
          .required("Property Sub-Category is required")
          .typeError("Property Sub-Category must be a number")
      : schema.notRequired();
  }),
  areaSqft: Yup.number()
    .positive("Must be a positive number")
    .required("Area is required"),

  propertyPremiseId: Yup.number().required("Premise Id is required"),
});

export const floorAddSchema = Yup.object().shape({
  floorNumber: Yup.number().required("Floor Number is required"),
  rentType: Yup.string()
    .required("Rent Type is required")
    .trim("Can't contain trailing spaces"),
  rentUsage: Yup.string()
    .required("Rent Usage is required")
    .trim("Can't contain trailing spaces"),
  type: Yup.string()
    .required("Floor type is required")
    .trim("Can't contain trailing spaces"),
  category: Yup.number().when("houseCategory", (houseCategory, schema) => {
    return trimSpaceAndLowerCase(houseCategory[0]) === "nonresidential"
      ? schema
          .required("Property Category is required")
          .typeError("Property Category must be a number")
      : schema.notRequired();
  }),
  subCategory: Yup.number().when("houseCategory", (houseCategory, schema) => {
    return trimSpaceAndLowerCase(houseCategory[0]) === "nonresidential"
      ? schema
          .required("Property Sub-Category is required")
          .typeError("Property Sub-Category must be a number")
      : schema.notRequired();
  }),
  areaSqft: Yup.number()
    .positive("Must be a positive number")
    .required("Area is required"),
});

export const selfAssessmentSchema = Yup.object().shape({
  ownerName: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Owner Name is required"),
  fatherName: Yup.string(),
  husbandName: Yup.string(),
  propertyAddress: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Property address is required"),
  mobileNumber: Yup.string()
    .trim("Can't contain trailing spaces")
    .matches(
      /^(?:(?:\+|0{0,2})91(\s*|[-])?|[0]?)?([6789]\d{2}([-]?)\d{3}([-]?)\d{4})$/,
      "Mobile number is not valid"
    )
    .required("Mobile number is required"),
  emailId: Yup.string()
    .email("Invalid email address")
    .trim("Can't contain trailing spaces"),

  zoneId: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Zone is required"),
  wardId: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Ward is required"),
  mohallaId: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Mohalla is required"),

  houseNumber: Yup.string(),

  plotArea: Yup.number().required("Plot area is required"),
  constType: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Construction type is required"),

  houseCategory: Yup.string()
    .required("House Category is required")
    .trim("Can't contain trailing spaces"),

  category: Yup.number().when("houseCategory", (houseCategory, schema) => {
    return trimSpaceAndLowerCase(houseCategory[0]) === "nonresidential"
      ? schema
          .required("Property Category is required")
          .typeError("Property Category must be a number")
      : schema.notRequired();
  }),
  subCategory: Yup.number().when("houseCategory", (houseCategory, schema) => {
    return trimSpaceAndLowerCase(houseCategory[0]) === "nonresidential"
      ? schema
          .required("Property Sub-Category is required")
          .typeError("Property Sub-Category must be a number")
      : schema.notRequired();
  }),

  occupancyStatus: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Occupancy status is required"),
  roadType: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Road type is required"),
  roadWidth: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Road width is required"),

  constructionYear: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Construction year is required"),

  // facilitiesAvailable: Yup.array().required("Facilities are required"),

  sewerageConnectionAvailable: Yup.boolean().required(
    "Sewer Connection should be selected"
  ),
  waterTaxApplicable: Yup.boolean().required("Water Tax should be selected"),
  sourceOfWater: Yup.string().notRequired(),

  isDisabled: Yup.boolean().required("This is required"),
  disabilityPercentage: Yup.number().when(
    "isDisabled",
    (isDisabled, schema) => {
      return isDisabled[0]
        ? schema
            .required("Disable Percentage is required")
            .positive("Should be positive disability")
            .max(100, "Max disability percentage can be 100%")
            .typeError("Disable Percentage must be a number")
        : schema.notRequired();
    }
  ),
  disabilityDocumentProof: Yup.string().when(
    "isDisabled",
    (isDisabled, schema) => {
      return isDisabled[0]
        ? schema.required(
            "Document proof is required when disability is applied"
          )
        : schema.notRequired();
    }
  ),
  isExDefensePersonalWidow: Yup.boolean().required("This is required"),
  exDefensePersonalDocumentProof: Yup.string().when(
    "isExDefensePersonalWidow",
    (isExDefensePersonalWidow, schema) => {
      return isExDefensePersonalWidow[0]
        ? schema.required("Document proof is required")
        : schema.notRequired();
    }
  ),
});

export const arrearsFormSchema = Yup.object().shape({
  finYear: Yup.date().required("Financial Year is required."),
  revisedArrearsHouseTax: Yup.number()
    .required("House Tax is required.")
    .min(0, "Tax can not be negative"),
  revisedArrearsSewerageTax: Yup.number()
    .required("Sewerage Tax is required.")
    .min(0, "Tax can not be negative"),
  revisedArrearsWaterTax: Yup.number()
    .required("Water Tax is required.")
    .min(0, "Tax can not be negative"),
  receiptNumber: Yup.string().required("Receipt Number is required."),
  remarks: Yup.string(),
});

export const paymentSchema = Yup.object().shape({
  transactionType: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Transaction mode is required"),
  type: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Payment type is required"),
  paidAmount: Yup.number()
    .typeError("Paid amount must be a number")
    .required("Paid amount is required")
    .positive("Paid amount must be positive"),

  ddNumber: Yup.string().when("transactionType", (transactionType, schema) => {
    return transactionType[0] === "DD"
      ? schema
          .required("DD number is required")
          .typeError("DD Number must be a string")
      : schema.notRequired();
  }),
  chequeNo: Yup.string().when("transactionType", (transactionType, schema) => {
    return transactionType[0] === "Cheque"
      ? schema
          .required("Cheque number is required")
          .typeError("Cheque number must be a string")
      : schema.notRequired();
  }),
  bankName: Yup.string().when("transactionType", (transactionType, schema) => {
    return ["DD"].includes(transactionType[0])
      ? schema
          .required("Bank name is required")
          .typeError("Bank name must be a string")
      : schema.notRequired();
  }),
  issuerName: Yup.string().when(
    "transactionType",
    (transactionType, schema) => {
      return ["Cheque", "DD"].includes(transactionType[0])
        ? schema
            .required("Issuer name is required")
            .typeError("Issuer name must be a string")
        : schema.notRequired();
    }
  ),
  ifscCode: Yup.string().when("transactionType", (transactionType, schema) => {
    return transactionType[0] === "Cheque"
      ? schema
          .required("IFSC Code is required")
          .typeError("IFSC Code must be a string")
      : schema.notRequired();
  }),
});

export const onlinePaymentFormSchema = Yup.object().shape({
  firstname: Yup.string().required("Name is required"),
  phone: Yup.string()
    .trim("Can't contain trailing spaces")
    .matches(
      /^(?:(?!0|91)(?:\+|0{0,2})91(\s*|[-])?|[6789]\d{2}([-]?)\d{3}([-]?)\d{4})$/,
      "Mobile number is not valid, it should be of 10 numbers only"
    )
    .required("Mobile number is required"),
  email: Yup.string()
    .email("Invalid email address")
    .trim("Can't contain trailing spaces"),
  type: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Type is required"),
});

export const neftPaymentFormSchema = Yup.object().shape({
  payerName: Yup.string().required("Name is required"),
  payerMobile: Yup.string()
    .trim("Can't contain trailing spaces")
    .matches(
      /^(?:(?!0|91)(?:\+|0{0,2})91(\s*|[-])?|[6789]\d{2}([-]?)\d{3}([-]?)\d{4})$/,
      "Mobile number is not valid, it should be of 10 numbers only"
    )
    .required("Mobile number is required"),
  refId: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Ref Id is required"),
  debitAccountNo: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Debit Account No is required"),
  ifscCode: Yup.string().trim("Can't contain trailing spaces").notRequired(),
  transactionType: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Transaction Type is required"),
  paymentType: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Payment Type is required"),
  amount: Yup.number()
    .required("Amount is required")
    .min(0, "Amount must be non-negative"),
});

export const adminEditSchema = Yup.object().shape(
  {
    ownerName: Yup.string().required("Owner name is required"),
    husbandName: Yup.string().when("fatherName", (fatherName, schema) => {
      return !fatherName[0]
        ? schema
            .required("Husband's name is required")
            .typeError("Husband name must be a string")
        : schema.notRequired();
    }),
    fatherName: Yup.string().when("husbandName", (husbandName, schema) => {
      return !husbandName[0]
        ? schema
            .required("Father's name is required")
            .typeError("Father name must be a string")
        : schema.notRequired();
    }),

    mobileNumber: Yup.string()
      .trim("Can't contain trailing spaces")
      .matches(
        /^(?:(?:\+|0{0,2})91(\s*|[-])?|[0]?)?([6789]\d{2}([-]?)\d{3}([-]?)\d{4})$/,
        "Mobile number is not valid"
      ),
    houseNumber: Yup.string(),
    PTINNo: Yup.string(),
  },
  ["fatherName", "husbandName"]
);

export const addOperatorSchema = Yup.object().shape({
  username: Yup.string().required("Username is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  mobile: Yup.string().required("Mobile is required"),
  password: Yup.string().required("Password is required"),
  designation: Yup.string().required("Designation is required"),
  city_id: Yup.string().required("City ID is required"),
});

export const addOfficialSchema = Yup.object({
  username: Yup.string().trim().required("This field is required"),
  email: Yup.string()
    .trim()
    .email("Invalid email format")
    .required("This field is required"),
  minValue: Yup.number()
    .min(0, "Min value cannot be less than 0.")
    .max(
      Number.MAX_SAFE_INTEGER,
      `Max value cannot exceed ${Number.MAX_SAFE_INTEGER}.`
    ),
  maxValue: Yup.number()
    .min(Yup.ref("minValue"), "Max value must be greater than Min value.")
    .max(
      Number.MAX_SAFE_INTEGER,
      `Max value cannot exceed ${Number.MAX_SAFE_INTEGER}.`
    ),
  password: Yup.string().trim().required("This field is required"),
  accessId: Yup.string().trim().required("This field is required"),
  accessType: Yup.string().trim().required("This field is required"),
  loginType: Yup.string().trim().required("This field is required"),
  cityId: Yup.string().trim().required("This field is required"),
  accessibleZones: Yup.array(),
  accessibleWards: Yup.array(),
});

export const editOfficialSchema = Yup.object({
  username: Yup.string().trim().required("This field is required"),
  email: Yup.string()
    .trim()
    .email("Invalid email format")
    .required("This field is required"),
  minValue: Yup.number()
    .min(0, "Min value cannot be less than 0.")
    .max(
      Number.MAX_SAFE_INTEGER,
      `Max value cannot exceed ${Number.MAX_SAFE_INTEGER}.`
    ),
  maxValue: Yup.number()
    .min(Yup.ref("minValue"), "Max value must be greater than Min value.")
    .max(
      Number.MAX_SAFE_INTEGER,
      `Max value cannot exceed ${Number.MAX_SAFE_INTEGER}.`
    ),
  accessId: Yup.string().trim().required("This field is required"),
  accessType: Yup.string().trim().required("This field is required"),
  accessibleZones: Yup.array(),
  accessibleWards: Yup.array(),
});

export const addWardSchema = Yup.object().shape({
  city_id: Yup.string().required("City is required"),
  zone_id: Yup.string().required("Zone is required"),
  ward_no: Yup.number()
    .required("Ward no is required")
    .positive()
    .min(1, "Ward Number must be more than 0"),
  ward_fullname: Yup.string().required("Ward Full Name is required"),
});

export const editWardSchema = Yup.object().shape({
  existingName: Yup.string().required("Ward existing full name is required"),
  updatedName: Yup.string()
    .required("Ward new full name is required")
    .notOneOf([Yup.ref("existingName"), null], "Can not be same as old name"),
});

export const addZoneSchema = Yup.object().shape({
  city_id: Yup.string().required("City is required"),
  zone_no: Yup.number()
    .required("Zone number is required")
    .positive()
    .min(1, "Zone number must have value greater than 0"),
});

export const addMohallaSchema = Yup.object().shape({
  name: Yup.string().required("Mohalla Name is required"),
  zone: Yup.string().required("Zone is required"),
  ward: Yup.string().required("Ward is required"),
  water_tax_availability: Yup.boolean(),
  sewer_tax_availability: Yup.boolean(),
  road_w_12_24: Yup.object().shape({
    rcc_rbc: Yup.number().required("Required Value"),
    other_pucca: Yup.number().required("Required Value"),
    kachha: Yup.number().required("Required Value"),
    vacant_land: Yup.number().required("Required Value"),
  }),
  road_w_l_12: Yup.object().shape({
    rcc_rbc: Yup.number().required("Required Value"),
    other_pucca: Yup.number().required("Required Value"),
    kachha: Yup.number().required("Required Value"),
    vacant_land: Yup.number().required("Required Value"),
  }),
  road_w_m_24: Yup.object().shape({
    rcc_rbc: Yup.number().required("Required Value"),
    other_pucca: Yup.number().required("Required Value"),
    kachha: Yup.number().required("Required Value"),
    vacant_land: Yup.number().required("Required Value"),
  }),
});

export const editMohallaSchema = Yup.object().shape({
  mohallaOldName: Yup.string().required("Mohalla Old Name is required"),
  mohallaNewName: Yup.string().notRequired("Mohalla New Name is required"),
  zone: Yup.string().required("Zone is required"),
  ward: Yup.string().required("Ward is required"),
  water_tax_availability: Yup.boolean().required("Required Value"),
  sewer_tax_availability: Yup.boolean().required("Required Value"),
  road_w_12_24: Yup.object().shape({
    rcc_rbc: Yup.number().required("Required Value"),
    other_pucca: Yup.number().required("Required Value"),
    kachha: Yup.number().required("Required Value"),
    vacant_land: Yup.number().required("Required Value"),
  }),
  road_w_l_12: Yup.object().shape({
    rcc_rbc: Yup.number().required("Required Value"),
    other_pucca: Yup.number().required("Required Value"),
    kachha: Yup.number().required("Required Value"),
    vacant_land: Yup.number().required("Required Value"),
  }),
  road_w_m_24: Yup.object().shape({
    rcc_rbc: Yup.number().required("Required Value"),
    other_pucca: Yup.number().required("Required Value"),
    kachha: Yup.number().required("Required Value"),
    vacant_land: Yup.number().required("Required Value"),
  }),
});

export const addDiscountSchema = Yup.object().shape({
  billingPeriod: Yup.string().required("Billing Period is required"),
  status: Yup.boolean(),
  discountPercentage: Yup.number()
    .required("Discount Percentage is required")
    .min(0, "Discount percentage must be non-negative")
    .max(100),
  periodDescription: Yup.string().required("Period Description is required"),
  additionDiscountCriteria: Yup.array().of(
    Yup.object().shape({
      criteria: Yup.string(),
      discountPercentage: Yup.number()
        .min(0, "Discount percentage cannot be less than 0.")
        .max(100),
    })
  ),
});

export const editDiscountSchema = Yup.object().shape({
  billingPeriod: Yup.string().required("Billing Period is required"),
  status: Yup.boolean(),
  discountPercentage: Yup.number()
    .required("Discount Percentage is required")
    .min(0, "Discount percentage must be non-negative"),
  periodDescription: Yup.string().required("Period Description is required"),
  additionDiscountCriteria: Yup.array().of(
    Yup.object().shape({
      criteria: Yup.string(),
      discountPercentage: Yup.number()
        .min(0, "Discount percentage cannot be less than 0.")
        .max(100),
    })
  ),
});

export const photoUpdateSchema = Yup.object().shape({
  buildingImageFront: Yup.mixed()
    .required("Building front-view is required")
    .test("is-valid-type", "Not a valid image type", (value) =>
      isValidFileType(value && value.name.toLowerCase(), "image")
    ),
  buildingImageRight: Yup.mixed()
    .required("Building right-view is required")
    .test("is-valid-type", "Not a valid image type", (value) =>
      isValidFileType(value && value.name.toLowerCase(), "image")
    ),
  buildingImageLeft: Yup.mixed()
    .required("Building left-view is required")
    .test("is-valid-type", "Not a valid image type", (value) =>
      isValidFileType(value && value.name.toLowerCase(), "image")
    ),
  ownerId: Yup.mixed().notRequired(),
  landRegistryDocument: Yup.mixed().notRequired(),
  electricityBill: Yup.mixed().notRequired(),
});

export const requestMutationSchema = Yup.object().shape(
  {
    ownerName: Yup.string().required("Owner name is required"),
    husbandName: Yup.string().when("fatherName", (fatherName, schema) => {
      return !fatherName[0]
        ? schema
            .required("Husband's name is required")
            .typeError("Husband name must be a string")
        : schema.notRequired();
    }),
    fatherName: Yup.string().when("husbandName", (husbandName, schema) => {
      return !husbandName[0]
        ? schema
            .required("Father's name is required")
            .typeError("Father name must be a string")
        : schema.notRequired();
    }),
  },
  ["fatherName", "husbandName"]
);

export const oldPropertyAddSchema = Yup.object().shape({
  ownerName: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Owner Name is required"),
  oldZoneId: Yup.number()
    .typeError("The field should be a number")
    .required("Zone Id is required"),
  oldWardId: Yup.number()
    .typeError("The field should be a number")
    .required("Ward Id is required"),
  oldMohallaId: Yup.number()
    .typeError("The field should be a number")
    .required("Mohalla Id is required"),
  oldZoneName: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Zone Name is required"),
  oldWardName: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Ward Name is required"),
  oldMohallaName: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Mohalla Name is required"),
  PTINNo: Yup.string()
    .min(6, "PTINNo must be at least 6 characters")
    .required("PTINNo is required"),
  ownerFatherName: Yup.string().trim("Can't contain trailing spaces"),
  oldMobileNo: Yup.string()
    .trim("Can't contain trailing spaces")
    .matches(
      /^(?:(?:\+|0{0,2})91(\s*|[-])?|[0]?)?([6789]\d{2}([-]?)\d{3}([-]?)\d{4})$/,
      "Mobile number is not valid"
    ),
  oldHouseNo: Yup.string(),
  oldOwnerAddress: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Property address is required"),
  oldConstYear: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Construction year is required"),
  oldBuildingUsage: "",
  oldHouseCategory: Yup.string()
    .required("House Category is required")
    .trim("Can't contain trailing spaces"),
  oldConstructionArea: Yup.number()
    .min(0, "Must be a positive number or 0")
    .required("Construction area is required"),
  oldPlotArea: Yup.number().required("Plot area is required"),
  oldOpenArea: Yup.number()
    .min(0, "Must be a positive number or 0")
    .required("Open area is required"),
  oldCarpetArea: Yup.number()
    .min(0, "Must be a positive number or 0")
    .required("Carpet area is required"),
  oldArv: Yup.number()
    .positive("Must be a positive number or 0")
    .required("ARV is required"),
  houseTaxApplicable: Yup.boolean().required("House Tax should be selected"),
  arrearsHouseTax: Yup.number().min(0, "Must be a positive number or 0"),
  interestHouseTax: Yup.number().min(0, "Must be a positive number or 0"),
  oldHouseTax: Yup.number().min(0, "Must be a positive number or 0"),
  waterTaxApplicable: Yup.boolean().required("Water Tax should be selected"),
  arrearsWaterTax: Yup.number().min(0, "Must be a positive number or 0"),
  interestWaterTax: Yup.number().min(0, "Must be a positive number or 0"),
  oldWaterTax: Yup.number().min(0, "Must be a positive number or 0"),
  sewerTaxApplicable: Yup.boolean().required("Sewer Tax should be selected"),
  arrearsSewarageTax: Yup.number().min(0, "Must be a positive number or 0"),
  interestSewerTax: Yup.number().min(0, "Must be a positive number or 0"),
  oldSewerTax: Yup.number().min(0, "Must be a positive number or 0"),
  oldConstType: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Construction type is required"),
  oldRoadWidth: Yup.string()
    .trim("Can't contain trailing spaces")
    .required("Road width is required"),
  arrearsTotalTax: Yup.number().min(0, "Must be a positive number or 0"),
});

export const paymentShiftSchema = Yup.object().shape({
  paidPUID: Yup.string()
    .required("Unique Property ID is required")
    .trim("Can't contain trailing spaces"),
  shiftPUID: Yup.string()
    .required("Unique Property ID is required")
    .trim("Can't contain trailing spaces"),
  requestedBy: Yup.string().required("Requested By is required"),
});

export const propertyDeletionSchema = Yup.object().shape({
  propertyIds: Yup.array()
    .of(
      Yup.string()
        .length(17, "Each Property ID must be exactly 17 characters long")
        .matches(
          /^[a-zA-Z0-9-]+$/,
          "Each Property ID must be alphanumeric with optional hyphens"
        )
        .required("Property ID cannot be empty")
    )
    .min(1, "At least one Property ID is required"),
  reason: Yup.string()
    .required("Reason is required")
    .min(5, "Reason must be at least 5 characters long"),
  requestedBy: Yup.string()
    .required("Requested By is required")
    .matches(
      /^[a-zA-Z\s]+$/,
      "Requested By must contain only letters and spaces"
    ),
});

export const paymentCancelSchema = Yup.object().shape({
  txnID: Yup.string()
    .required("Transaction ID is required")
    .trim("Can't contain trailing spaces"),
  remarks: Yup.string()
    .required("Remark is required")
    .min(5, "Remark must be at least 5 characters long"),
});
